import type {Deal, ProductDeals} from '../models/ProductDeals';
import type {ProductPrice} from '../../catalog/models/ProductPrice';
import type {BusinessCentralDiscounts} from '../models/BusinessCentralDiscounts';
import {Spinner} from 'react-bootstrap';
import {PriceBreakdownUI} from './PriceBreakdownUI';
import {AddToCart} from '../../cart/components/add-to-cart/AddToCart';
import {DealType} from '../models/DealSelection';
import React from 'react';
import {DealSelector} from '../features/DealSelector';
import type {ProductDetails} from '../models/ProductDetails';
import {NumberInputUI} from '../../ui/components/NumberInputUI';
import {ProductPriceUI} from './ProductPriceUI';
import {AvailabilityUI} from '../../common/ui/AvailabilityUI';
import {useEnhancedItem} from '../../catalog/application/useEnhancedItem';

interface Props {
  data: {
    deals: ProductDeals,
    loading: boolean,
    dealSelection: Deal,
    quantity: number,
    price: ProductPrice,
    discount: string,
    bcDiscounts: BusinessCentralDiscounts,
    details: ProductDetails;
  };
  actions: {
    onQuantityChange: (quantity: number) => void,
    onDealSelectionChange: (deal: Deal) => void,
    afterAddToCart: () => void,
  };
}

export const ProductPurchasePanelUI = ({ data, actions }: Props) => {
  const {
    deals,
    loading,
    error,
    dealSelection,
    quantity,
    price,
    discount,
    bcDiscounts,
    details
  } = data;

  const enhancedItem = useEnhancedItem(details, quantity);

  if (loading === true) {
    return <div className="text-center m-4"><Spinner /></div>;
  }

  if (error) {
    return <div className="alert alert-danger m-2">Imposible de cargar precios y ofertas.</div>;
  }

  return (
    <div>
      <div className="mb-2 d-flex flex-row align-items-bottom gap-2">
          <AvailabilityUI data={{ availability: enhancedItem.availability }} />
      </div>
      <div className="d-flex flex-row justify-content-start align-items-end gap-2">
        <div className="d-flex align-items-center gap-2">
          {
            !!price && <span>Precio Unidad: </span>
          }
          <ProductPriceUI price={price} />
        </div>
      </div>
      {
        !!discount?.text && (
          <p>Porcentaje de ahorro: <strong className="text-primary">{discount?.text}</strong></p>
        )
      }
      <hr />
      {
        (deals?.OFERTASEXCLUSIVAS?.length > 0 || deals?.LOTES?.length > 0 || deals?.PROMOCIONES?.length > 0) && (
          <>
            <DealSelector productDeals={deals}
                          selection={dealSelection}
                          onSelectionChange={actions.onDealSelectionChange}
            />
            <hr />
          </>
        )
      }
      <div className="row justify-content-end align-items-center gap-2 gap-sm-0">
        {
          price && (
            <div className="col-auto">
              <PriceBreakdownUI
                price={{
                  sellingPrice: price.sellingPrice * quantity,
                  basePrice: price.basePrice * quantity
                }}
                discount={discount}
              />
            </div>
          )
        }
        <div className="col-auto">
          <NumberInputUI data={{ value: quantity }} actions={{ setValue: (value) => actions.onQuantityChange(value) }} />
        </div>
        <div className="col-auto">
          <AddToCart
            data={{
              details: details,
              bcDiscounts: bcDiscounts,
              price: price,
              quantity: quantity,
              giftProductDeal: dealSelection?.type === DealType.GIFT_DEAL && dealSelection.value,
              disabled: !!(price?.loading) || !price || price?.sellingPrice <= 0 || quantity <= 0
            }}
            actions={{
              afterAddToCart: actions.afterAddToCart
            }}
          />
        </div>
      </div>
    </div>
  );
};